// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The key guide: a rail beside the machine that says what the keyboard does (M3-56, 08 §4.3).
//
// `keyGuide.ts` holds every decision this makes and `input/desktop.ts` holds every fact it draws.
// What is here is markup, one piece of state, and the two things that can only be got wrong in a
// component.
//
// THE FIRST: A DISCLOSURE BUTTON ON A MACHINE WHOSE PLUNGER IS SPACE
// ------------------------------------------------------------------
// A `button` element is activated by **Space and Enter**, and both are launch keys. So a player
// who clicks "Hide" and then reaches for the plunger fires the guide instead of the ball, on a
// machine where the plunger's whole skill is how long the key is held. The same trap is why
// `desktop.ts` guards `isTyping` — a text field swallowing Space is the same defect one surface
// over — and it is the shape of the touch bug 08 §5.2a records, where making the flipper zones
// real controls would have given "flippers that work with a mouse and do nothing under a thumb".
//
// **So activating the toggle hands focus back to the stage.** Not `blur()`, which would drop a
// keyboard player out of the tab order at the point they were in it; the stage carries
// `tabIndex={-1}` and becomes the focused element, which is where somebody who just pressed a
// control on a pinball machine wants to be. Tab from there continues into the shell as before.
//
// THE SECOND: COLLAPSED HAS TO MEAN GONE
// --------------------------------------
// `hidden` rather than a class that sets `display: none`, for the reason `playFocus.ts` gives
// about the navigation rows: a panel hidden with CSS is still in the accessibility tree, so a
// screen-reader user would be read a control legend that is not on the screen and cannot be seen.
// `aria-expanded` and `aria-controls` then describe a real relationship rather than an aspiration.

import { useState } from 'react'
import { GUIDE_ROWS, keycapFor, type GuideRow } from '../keyGuide'
import { keyGuideStartsOpen, writeKeyGuideChoice } from '../firstRun'
import { useLocalisation } from '../i18n/useLanguage'
import type { Translator } from '../i18n'

/** The id the toggle points `aria-controls` at. One panel per page, so a constant is honest. */
const PANEL_ID = 'key-guide-rows'

function Keycaps({ row, t }: { readonly row: GuideRow; readonly t: Translator }): React.JSX.Element {
  return (
    <span className="keyGuide__caps">
      {row.caps.map((code) => {
        const cap = keycapFor(code)
        return (
          <kbd className="keyGuide__cap" key={code}>
            {cap.kind === 'glyph' ? cap.glyph : t(cap.word)}
          </kbd>
        )
      })}
    </span>
  )
}

/**
 * The rows themselves, with no panel around them.
 *
 * **Separate from the rail because two surfaces need the same list.** The Builder's test-play panel
 * carried its own hand-written sentence about the keys — which had already drifted: it named no
 * pause key, no `W`/`S` nudge, and said "Space is the plunger" without ever saying that holding it
 * is what sets the power. That is precisely the second copy this task exists to delete, so it now
 * renders this.
 */
export function KeyGuideList({ id }: { readonly id?: string }): React.JSX.Element {
  const { t } = useLocalisation()
  return (
    <ul className="keyGuide__rows" id={id}>
      {GUIDE_ROWS.map((row) => (
        <li
          // `held` is the binding table's own word for "how long this key is down is part of what
          // it means", and it is the one row on the panel that has to read differently from the
          // others — so it carries a class rather than being described only in a sentence.
          className={row.held ? 'keyGuide__row keyGuide__row--held' : 'keyGuide__row'}
          key={row.id}
        >
          <span className="keyGuide__label">{t(row.label)}</span>
          <Keycaps row={row} t={t} />
          {/* The note is the reason the panel exists on the plunger's row, so it is not a
              tooltip: a sentence a player has to hover to find is a sentence for somebody who
              already knows it is there. */}
          {row.note === undefined ? null : (
            <span className="keyGuide__note">{t(row.note)}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

export interface KeyGuideProps {
  /**
   * Where focus goes after the toggle is pressed. The stage, in the shell.
   *
   * Optional because a surface with no machine on it — the Builder's panel — has nowhere to send
   * focus and does not draw the toggle at all.
   */
  readonly playSurface?: HTMLElement | null
  /**
   * How far above the bottom of the pane to sit, in CSS pixels.
   *
   * **From the layout, never from the stylesheet** — the same rule `--bat-size` and `--lane` are
   * already written under. The drawn left flipper is anchored to the bottom-left corner and its
   * size is a function of the zone width, so a rail with a fixed offset covers the bat on one
   * window and floats above it on another. Measured on a 1347 × 762 pane: a 12 px offset put 122 ×
   * 73 px of this panel on top of the bat.
   */
  readonly liftAboveTheCabinet?: number
  /**
   * The tallest it may be, in CSS pixels, before it reaches the HUD.
   *
   * The rows scroll inside it rather than the rail growing past what the pane has spare — see
   * `keyGuideHeadroom`, which measures the HUD instead of assuming it.
   */
  readonly headroom?: number
}

/** The collapsible rail. */
export function KeyGuide({
  playSurface,
  liftAboveTheCabinet,
  headroom,
}: KeyGuideProps): React.JSX.Element {
  const { t } = useLocalisation()
  // Read once at mount, like every other `firstRun` answer: this is what the player last chose,
  // and re-reading storage on a render would let a second tab change a panel under their hand.
  const [open, setOpen] = useState(keyGuideStartsOpen)

  const toggle = (): void => {
    const next = !open
    setOpen(next)
    writeKeyGuideChoice(next ? 'open' : 'closed')
    // See the header: Space and Enter both activate a focused button and both launch the ball.
    playSurface?.focus()
  }

  // No `--open` modifier class: `hidden` on the panel below is what open and closed *are*, and a
  // class duplicating it would be a second answer to one question — and a class with no rule
  // behind it, which `styles.test.ts` exists to catch.
  return (
    <aside
      className="keyGuide"
      style={{
        ...(liftAboveTheCabinet === undefined ? {} : { bottom: liftAboveTheCabinet }),
        ...(headroom === undefined ? {} : { maxHeight: headroom }),
      }}
    >
      <button
        type="button"
        className="button button--quiet keyGuide__toggle"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        title={open ? t('guide.hide-title') : t('guide.show-title')}
      >
        <span className="keyGuide__title">{t('guide.title')}</span>
        <span className="keyGuide__chevron" aria-hidden="true">
          {open ? '×' : '?'}
        </span>
      </button>
      <div hidden={!open}>
        <KeyGuideList id={PANEL_ID} />
      </div>
    </aside>
  )
}
